// randomPatient.js
// Shared helpers for generating fictitious test patients. Originally lived
// inside StudentEpmaWorkspace.jsx; pulled out so the spot-the-error editor
// (and any other authoring surface that needs a quick "random patient" fill)
// can reuse the same name / address pool the EPMA simulator uses.

import data from '../components/casestudy_editor/randomFields.json';

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

export const buildRandomAddress = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVQXYZ';
  const postcode = `${characters[Math.floor(Math.random() * characters.length)]}${characters[Math.floor(Math.random() * characters.length)]}${Math.ceil(Math.random() * 10)} ${Math.ceil(Math.random() * 10)}${characters[Math.floor(Math.random() * characters.length)]}${characters[Math.floor(Math.random() * characters.length)]}`;
  const houseNo = Math.ceil(Math.random() * 120);
  const street = pick(data.addresses.streets);
  const town = pick(data.addresses.towns);
  return `${houseNo} ${street}, ${town}, ${postcode}`;
};

const stayTypeWardOptions = {
  'A/E': ['Majors', 'Resus', 'Assessment', 'Ambulatory Emergency Care'],
  'Ward inpatient': ['Cedar Ward', 'Willow Ward', 'Maple Ward', 'Oak Ward', 'Rose Ward'],
  Daycase: ['Day Surgery Unit', 'Medical Day Unit', 'Endoscopy Day Unit', 'Ambulatory Care Unit'],
  Theatre: ['Theatre Suite 1', 'Theatre Suite 2', 'Recovery Bay', 'Surgical Admissions Lounge'],
};

export const buildRandomWardName = (stayType) => {
  const options = stayTypeWardOptions[stayType] || stayTypeWardOptions['Ward inpatient'];
  return pick(options);
};

// EPMA-shaped builder. Caller supplies the controlled fields (gender, age,
// weight, height, stayType) and we fill the random name + address + ward +
// DOB derived from age. Kept compatible with the original inline helper.
export const buildRandomPatient = ({ gender, ageYears, weightKg, heightCm, stayType }) => {
  const nameBucket = gender === 'Male' ? 'male_names' : 'female_names';
  const firstName = pick(data.names[nameBucket]);
  const surname = pick(data.names.surnames);
  const today = new Date();
  const month = Math.floor(Math.random() * 12);
  const day = Math.max(1, Math.floor(Math.random() * 28));
  const dob = new Date(today.getFullYear() - Number(ageYears || 30), month, day);

  return {
    fullName: `${firstName} ${surname}`,
    dateOfBirth: dob.toLocaleDateString('en-GB'),
    address: buildRandomAddress(),
    gender,
    stayType,
    wardName: buildRandomWardName(stayType),
    weight: `${weightKg}kg`,
    height: `${heightCm}cm`,
  };
};

// Plausible 10-digit NHS number. Not Modulus-11 valid (intentional —
// these are training records, never used for a real patient lookup) but
// formatted with the standard "xxx xxx xxxx" grouping so it reads right
// on the outpatient prescription form.
const generateTrainingNhsNumber = () => {
  let digits = '';
  for (let i = 0; i < 10; i += 1) digits += String(randInt(0, 9));
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

// Convenience for authoring surfaces (spot-the-error / med-rec / drug
// history / outpatient editors) that want a plausible patient with
// nothing pre-filled. Rolls every field within sensible adult bounds and
// returns a rich object the caller can cherry-pick from — keys are named
// so each editor can map straight into its own patient shape without
// further reshaping.
export const generateExercisePatient = () => {
  const gender = Math.random() < 0.5 ? 'Male' : 'Female';
  const ageYears = randInt(20, 88);
  const weightKg = randInt(48, 110);
  const heightCm = gender === 'Male' ? randInt(165, 190) : randInt(150, 178);
  const stayType = pick(['Ward inpatient', 'A/E']);
  const built = buildRandomPatient({ gender, ageYears, weightKg, heightCm, stayType });
  return {
    // Spot-the-error / med-rec / drug history shape
    name: built.fullName,
    age: String(ageYears),
    ageYears,
    gender,
    weight: `${weightKg}kg`,
    weightKg,
    heightCm,
    // Outpatient shape
    dob: built.dateOfBirth,
    trainingNhsNumber: generateTrainingNhsNumber(),
    trainingAddress: built.address,
    // Building blocks any caller may want to compose
    wardName: built.wardName,
    stayType,
    address: built.address,
    otherDetails: `${built.wardName} (${stayType}) · ${heightCm}cm · DOB ${built.dateOfBirth} · ${built.address}`,
  };
};
